import React from 'lib/react';

import {
  EVGrid,
  EVInputLabel,
  EVButton,
  EVCheckbox,
} from 'lib/components';
import { useTranslation } from 'lib/translation';
import PropTypes from 'prop-types';

import { COUNTRY_LIST } from 'constants/AppConstants';
import TextInputField from 'commonComponents/textInputField';
import SelectField from 'commonComponents/select-field';
import MaskedPhoneNumber from 'commonComponents/maskedPhoneNumber';
import ContactAddressInformationStyles from './ContactAddressInformationStyles';
import { getStateList } from '../../../../utils/validation.util';
import ContactAddressInformationLoader from './components/ContactAddressInformationLoader/ContactAddressInformationLoader.component';

export default function ContactAddressInformation({
  isLoading,
  addressInfo,
  addressInfoErrors,
  handleChange,
  onCancel,
  inputRefs,
}) {
  if (isLoading) {
    return (
      <ContactAddressInformationLoader />
    );
  }
  const classes = ContactAddressInformationStyles();
  const { t } = useTranslation();
  const validateFieldAndHandleChange = (
    fieldId,
    { target: { id, value } },
  ) => {
    const targetId = fieldId || id;
    if (targetId === 'country') {
      handleChange('state', '');
    }
    handleChange(targetId, value);
  };

  return (
    <EVGrid container item direction="column">
      <EVGrid className={classes.fieldContainer} item>
        <TextInputField
          inputId="firstName"
          inputValue={addressInfo.firstName}
          size="small"
          handleChange={(event) => validateFieldAndHandleChange(
            'firstName',
            event,
          )}
          inputLabelProps={{
            className: classes.label,
            classes: { asterisk: classes.asterisk },
            required: true,
            error: addressInfoErrors.firstName.length > 0,
          }}
          inputFieldProps={{
            fullWidth: true,
            inputProps: { maxLength: 400 },
            classes: { root: classes.addressInputText },
            FormHelperTextProps: { classes: { error: classes.addressInputHelperText } },
            inputRef: inputRefs.firstName,
          }}
          labelText={t('contact_information__address_information__firstName')}
          helperText={addressInfoErrors.firstName ? t('common__field_required') : ''}
        />
      </EVGrid>
      <EVGrid className={classes.fieldContainer} item>
        <TextInputField
          inputId="lastName"
          inputValue={addressInfo.lastName}
          size="small"
          handleChange={(event) => validateFieldAndHandleChange(
            'lastName',
            event,
          )}
          inputLabelProps={{
            className: classes.label,
            classes: { asterisk: classes.asterisk },
            required: true,
            error: addressInfoErrors.lastName.length > 0,
          }}
          inputFieldProps={{
            fullWidth: true,
            inputProps: { maxLength: 400 },
            classes: { root: classes.addressInputText },
            FormHelperTextProps: { classes: { error: classes.addressInputHelperText } },
            inputRef: inputRefs.lastName,
          }}
          labelText={t('contact_information__address_information__lastName')}
          helperText={addressInfoErrors.lastName ? t('common__field_required') : ''}
        />
      </EVGrid>
      <EVGrid className={classes.fieldContainer} item>
        <TextInputField
          inputId="company"
          inputValue={addressInfo.company}
          size="small"
          handleChange={(event) => validateFieldAndHandleChange(
            'company',
            event,
          )}
          inputLabelProps={{
            className: classes.label,
            classes: { asterisk: classes.asterisk },
            required: true,
            error: addressInfoErrors.company.length > 0,
          }}
          inputFieldProps={{
            fullWidth: true,
            inputProps: { maxLength: 166 },
            classes: { root: classes.addressInputText },
            FormHelperTextProps: { classes: { error: classes.addressInputHelperText } },
            inputRef: inputRefs.company,
          }}
          labelText={t('contact_information__address_information_company')}
          helperText={addressInfoErrors.company ? t('common__field_required') : ''}
        />
      </EVGrid>
      <EVGrid className={classes.fieldContainer} item>
        <EVInputLabel className={classes.label}>
          {t('contact_information__address_information_department')}
          <b className={classes.optionalLabel}>
            {t('contact_information__address_information_optional')}
          </b>
        </EVInputLabel>
        <TextInputField
          inputId="department"
          inputValue={addressInfo.department}
          size="small"
          handleChange={(event) => validateFieldAndHandleChange('department', event)}
          inputLabelProps={{
            className: classes.label,
            classes: { asterisk: classes.asterisk },
            error: addressInfoErrors.department.length > 0,
          }}
          inputFieldProps={{
            fullWidth: true,
            inputProps: { maxLength: 166 },
            classes: { root: classes.addressInputText },
            FormHelperTextProps: { classes: { error: classes.addressInputHelperText } },
          }}
          helperText={addressInfoErrors.department}
        />
      </EVGrid>
      <EVGrid container item direction="column" style={{ marginBottom: '16px' }}>
        <EVGrid className={classes.fieldContainer} item style={{ marginBottom: 0 }}>
          <TextInputField
            inputId="emailAddress"
            inputValue={addressInfo.emailAddress}
            size="small"
            handleChange={(event) => validateFieldAndHandleChange(
              'emailAddress',
              event,
            )}
            inputLabelProps={{
              className: classes.label,
              classes: { asterisk: classes.asterisk },
              required: true,
              error: addressInfoErrors.emailAddress && addressInfoErrors.emailAddress.length > 0,
            }}
            inputFieldProps={{
              fullWidth: true,
              inputProps: { maxLength: 255 },
              classes: { root: classes.addressInputText },
              error: addressInfoErrors.emailAddress && addressInfoErrors.emailAddress.length > 0,
              FormHelperTextProps: {
                classes: { error: classes.addressInputHelperText },
                style: { color: '#82899C', fontSize: '14px' },
              },
              inputRef: inputRefs.emailAddress,
            }}
            labelText={t('contact_information__address_information_email')}
            helperText={addressInfoErrors.emailAddress || t('contact_information__email_helper')}
          />
        </EVGrid>
        <EVGrid className={classes.fieldContainer} item style={{ display: 'flex', alignItems: 'center', marginBottom: 0 }}>
          <EVCheckbox
            checked={addressInfo.marketingEmails || false}
            onChange={(event) => validateFieldAndHandleChange('marketingEmails', {
              target: { value: event.target.checked },
            })}
            name="marketingEmails"
            id="marketingEmails"
            color="primary"
          />
          <EVInputLabel htmlFor="marketingEmails" className={classes.checkboxLabel}>
            {t('contact_information__marketing_emails_checkbox')}
          </EVInputLabel>
        </EVGrid>
        <EVGrid className={classes.fieldContainer} item style={{ display: 'flex', alignItems: 'center', marginBottom: 0 }}>
          <EVCheckbox
            checked={addressInfo.alternateReportEmail || false}
            onChange={(event) => validateFieldAndHandleChange('alternateReportEmail', {
              target: { value: event.target.checked },
            })}
            name="alternateReportEmail"
            id="alternateReportEmail"
            color="primary"
          />
          <EVInputLabel htmlFor="alternateReportEmail" className={classes.checkboxLabel}>
            {t('contact_information__recipient_emails_checkbox')}
          </EVInputLabel>
        </EVGrid>
        {addressInfo.alternateReportEmail && (
          <EVGrid className={classes.fieldContainer} item>
            <TextInputField
              inputId="emailCC"
              inputValue={addressInfo.emailCC}
              size="small"
              handleChange={(event) => validateFieldAndHandleChange(
                'emailCC',
                event,
              )}
              inputLabelProps={{
                className: classes.label,
                classes: { asterisk: classes.asterisk },
                required: true,
                error: addressInfoErrors.emailCC && addressInfoErrors.emailCC.length > 0,
              }}
              inputFieldProps={{
                fullWidth: true,
                inputProps: { maxLength: 255 },
                classes: { root: classes.addressInputText },
                error: addressInfoErrors.emailCC && addressInfoErrors.emailCC.length > 0,
                FormHelperTextProps: {
                  classes: { error: classes.addressInputHelperText },
                  style: { color: '#82899C', fontSize: '14px' },
                },
                inputRef: inputRefs.emailCC,
              }}
              labelText={t('contact_information__address_information_recipient_email')}
              helperText={addressInfoErrors.emailCC || t('contact_information__recipient_email_helper')}
            />
          </EVGrid>
        )}
      </EVGrid>
      <EVGrid container item className={classes.phoneContainer} direction="row">
        <EVGrid className={classes.fieldContainer} item style={{ marginBottom: 0 }}>
          <TextInputField
            inputId="phone"
            inputValue={addressInfo.phone}
            size="small"
            handleChange={(event) => validateFieldAndHandleChange(
              'phone',
              event,
            )}
            inputLabelProps={{
              className: classes.label,
              classes: { asterisk: classes.asterisk },
              required: true,
              error: addressInfoErrors.phone.length > 0,
            }}
            inputFieldProps={{
              fullWidth: true,
              inputProps: { maxLength: 12 },
              classes: { root: classes.phoneInputText },
              FormHelperTextProps: { classes: { error: classes.addressInputHelperText } },
              inputRef: inputRefs.phone,
            }}
            labelText={t('address_information__phone')}
            helperText={addressInfoErrors.phone ? t('common__field_required') : ''}
            InputProps={{
              inputComponent: MaskedPhoneNumber,
            }}
          />
        </EVGrid>
        <EVGrid className={classes.fieldContainer} item>
          <TextInputField
            inputId="phoneExt"
            inputValue={addressInfo.phoneExt}
            size="small"
            handleChange={(event) => validateFieldAndHandleChange(
              'phoneExt',
              event,
            )}
            inputLabelProps={{
              className: classes.label,
              classes: { asterisk: classes.asterisk },
              error: addressInfoErrors.phoneExt.length > 0,
            }}
            inputFieldProps={{
              fullWidth: true,
              inputProps: { maxLength: 5 },
              classes: { root: classes.phoneExtInputText },
              FormHelperTextProps: { classes: { error: classes.addressInputHelperText } },
            }}
            labelText={t('address_information__phone_ext')}
            helperText={addressInfoErrors.phoneExt}
          />
        </EVGrid>
      </EVGrid>
      <EVGrid container item className={classes.secondaryPhoneContainer} direction="row">
        <EVGrid className={classes.fieldContainer} item>
          <TextInputField
            inputId="secondaryPhone"
            inputValue={addressInfo.secondaryPhone}
            size="small"
            handleChange={(event) => validateFieldAndHandleChange(
              'secondaryPhone',
              event,
            )}
            inputLabelProps={{
              className: classes.label,
              error: addressInfoErrors.secondaryPhone.length > 0,
            }}
            inputFieldProps={{
              fullWidth: true,
              inputProps: { maxLength: 12 },
              classes: { root: classes.secondaryPhoneInputText },
              FormHelperTextProps: { classes: { error: classes.addressInputHelperText } },
            }}
            labelText={t('address_information__secondary_phone')}
            helperText={addressInfoErrors.secondaryPhone}
            InputProps={{
              inputComponent: MaskedPhoneNumber,
            }}
          />
        </EVGrid>
        <EVGrid className={classes.fieldContainer} item>
          <TextInputField
            inputId="secondaryExt"
            inputValue={addressInfo.secondaryExt}
            size="small"
            handleChange={(event) => validateFieldAndHandleChange(
              'secondaryExt',
              event,
            )}
            inputLabelProps={{
              className: classes.label,
              classes: { asterisk: classes.asterisk },
              error: addressInfoErrors.secondaryExt.length > 0,
            }}
            inputFieldProps={{
              fullWidth: true,
              inputProps: { maxLength: 5 },
              classes: { root: classes.phoneExtInputText },
              FormHelperTextProps: { classes: { error: classes.addressInputHelperText } },
            }}
            labelText={t('address_information__phone_ext')}
            helperText={addressInfoErrors.secondaryExt}
          />
        </EVGrid>
      </EVGrid>
      <EVGrid className={classes.fieldContainer} item>
        <EVInputLabel
          className={classes.label}
          classes={{ asterisk: classes.asterisk }}
          required
          error={addressInfoErrors.country.length > 0}
        >
          {t('address_information__country')}
        </EVInputLabel>
        <SelectField
          selectId="country"
          variant="outlined"
          fullWidth
          selectedValue={addressInfo.country}
          handleChange={(event) => {
            validateFieldAndHandleChange(
              'country',
              event,
            );
          }}
          className={classes.countryOptions}
          selectOptions={COUNTRY_LIST}
          error={addressInfoErrors.country.length > 0}
          errorMessage={addressInfoErrors.country ? t('common__field_required') : ''}
          innerRef={inputRefs.country}
          disabled
        />
      </EVGrid>
      <EVGrid className={classes.fieldContainer} item>
        <TextInputField
          inputId="address1"
          inputValue={addressInfo.address1}
          size="small"
          handleChange={(event) => validateFieldAndHandleChange(
            'address1',
            event,
          )}
          inputLabelProps={{
            className: classes.label,
            classes: { asterisk: classes.asterisk },
            required: true,
            error: addressInfoErrors.address1.length > 0,
          }}
          inputFieldProps={{
            fullWidth: true,
            inputProps: { maxLength: 300 },
            classes: { root: classes.addressInputText },
            FormHelperTextProps: { classes: { error: classes.addressInputHelperText } },
            inputRef: inputRefs.address1,
          }}
          labelText={t('contact_information__address_information_address1')}
          helperText={addressInfoErrors.address1 ? t('common__field_required') : ''}
        />
      </EVGrid>
      <EVGrid className={classes.fieldContainer} item>
        <TextInputField
          inputId="address2"
          inputValue={addressInfo.address2}
          size="small"
          handleChange={(event) => validateFieldAndHandleChange('address2', event)}
          inputLabelProps={{
            className: classes.label,
            classes: { asterisk: classes.asterisk },
          }}
          inputFieldProps={{
            fullWidth: true,
            inputProps: { maxLength: 300 },
            classes: { root: classes.addressInputText },
          }}
          labelText={t('contact_information__address_information_address2')}
          helperText={addressInfoErrors.address2}
        />
      </EVGrid>
      <EVGrid container item direction="row" className={classes.cityStateContainer}>
        <EVGrid className={classes.cityContainer} item>
          <TextInputField
            inputId="city"
            inputValue={addressInfo.city}
            size="small"
            handleChange={(event) => validateFieldAndHandleChange(
              'city',
              event,
            )}
            inputLabelProps={{
              className: classes.label,
              classes: { asterisk: classes.asterisk },
              required: true,
              error: addressInfoErrors.city.length > 0,
            }}
            inputFieldProps={{
              fullWidth: true,
              inputProps: { maxLength: 80 },
              classes: { root: classes.addressInputText },
              FormHelperTextProps: { classes: { error: classes.addressInputHelperText } },
              inputRef: inputRefs.city,
            }}
            labelText={t('address_information__city')}
            helperText={addressInfoErrors.city ? t('common__field_required') : ''}
          />
        </EVGrid>
        <EVGrid className={classes.stateContainer} item>
          <EVInputLabel
            className={classes.label}
            classes={{ asterisk: classes.asterisk }}
            required
            error={addressInfoErrors.state.length > 0}
          >
            {t('address_information__state')}
          </EVInputLabel>
          <SelectField
            selectId="state"
            variant="outlined"
            fullWidth
            selectedValue={addressInfo.state}
            handleChange={
              (event) => validateFieldAndHandleChange(
                'state',
                event,
              )
            }
            className={classes.stateOptions}
            selectOptions={getStateList(addressInfo.country)}
            error={addressInfoErrors.state.length > 0}
            errorMessage={addressInfoErrors.state ? t('common__field_required') : ''}
            innerRef={inputRefs.state}
          />
        </EVGrid>
        <EVGrid className={classes.zipContainer} item>
          <TextInputField
            inputId="zip"
            inputValue={addressInfo.zip}
            size="small"
            handleChange={(event) => validateFieldAndHandleChange(
              'zip',
              event,
            )}
            inputLabelProps={{
              className: classes.label,
              classes: { asterisk: classes.asterisk },
              required: true,
              error: addressInfoErrors.zip.length > 0,
            }}
            inputFieldProps={{
              fullWidth: true,
              inputProps: { maxLength: 80 },
              classes: { root: classes.addressInputText },
              FormHelperTextProps: { classes: { error: classes.addressInputHelperText } },
              inputRef: inputRefs.zip,
            }}
            labelText={t('address_information__zip')}
            helperText={addressInfoErrors.zip ? t('common__field_required') : ''}
          />
        </EVGrid>
      </EVGrid>
      <EVGrid container wrap="nowrap" className={classes.buttonContainer}>
        <EVButton
          type="submit"
          id="submitContactInfo"
          variant="contained"
          style={{ backgroundColor: '#007CAD', color: '#FFFFFF' }}
          className={classes.button}
        >
          {t('common__save')}
        </EVButton>
        <EVButton data-testid="cancelButton" onClick={onCancel} variant="outlined" color="primary" className={classes.button}>
          {t('address_information__cancel')}
        </EVButton>
      </EVGrid>
    </EVGrid>
  );
}

ContactAddressInformation.defaultProps = {
  inputRefs: null,
};

ContactAddressInformation.propTypes = {
  isLoading: PropTypes.bool.isRequired,
  addressInfo: PropTypes.shape().isRequired,
  addressInfoErrors: PropTypes.shape().isRequired,
  handleChange: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  inputRefs: PropTypes.shape(),
};
